
import React from 'react';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { BookingLink } from '@/types/booking';
import { Video, Monitor, Phone, MapPin } from 'lucide-react';

interface MeetingTypeSelectorProps {
  meetingTypes: BookingLink['meetingTypes'];
  selectedType: string;
  onSelect: (type: string) => void;
  locations?: string[];
}

const MeetingTypeSelector: React.FC<MeetingTypeSelectorProps> = ({
  meetingTypes,
  selectedType,
  onSelect,
  locations
}) => {
  // Filter to only enabled meeting types
  const enabledTypes = Object.entries(meetingTypes)
    .filter(([_, enabled]) => enabled)
    .map(([type]) => type);

  if (enabledTypes.length === 0) return null;

  return (
    <Card className="p-6 mb-6">
      <h3 className="text-lg font-medium mb-4">Select Meeting Type</h3>

      <RadioGroup 
        value={selectedType} 
        onValueChange={onSelect}
        className="space-y-3"
      >
        {meetingTypes.zoom && (
          <div className="flex items-start space-x-2 border border-input rounded-md p-3 hover:border-primary/50 transition-all cursor-pointer">
            <RadioGroupItem value="zoom" id="zoom" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="zoom" className="flex items-center cursor-pointer">
                <Video className="h-4 w-4 mr-2 text-primary" />
                <span>Zoom Meeting</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                A video call using Zoom
              </p>
            </div>
          </div>
        )}

        {meetingTypes.googleMeet && (
          <div className="flex items-start space-x-2 border border-input rounded-md p-3 hover:border-primary/50 transition-all cursor-pointer">
            <RadioGroupItem value="googleMeet" id="googleMeet" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="googleMeet" className="flex items-center cursor-pointer">
                <Monitor className="h-4 w-4 mr-2 text-primary" />
                <span>Google Meet</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                A video call using Google Meet
              </p>
            </div>
          </div>
        )}

        {meetingTypes.phone && (
          <div className="flex items-start space-x-2 border border-input rounded-md p-3 hover:border-primary/50 transition-all cursor-pointer">
            <RadioGroupItem value="phone" id="phone" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="phone" className="flex items-center cursor-pointer">
                <Phone className="h-4 w-4 mr-2 text-primary" />
                <span>Phone Call</span>
              </Label>
              <p className="text-xs text-muted-foreground mt-1 ml-6">
                A direct phone call to your number
              </p>
            </div>
          </div>
        )}

        {meetingTypes.inPerson && locations && locations.length > 0 && (
          <div className="flex items-start space-x-2 border border-input rounded-md p-3 hover:border-primary/50 transition-all cursor-pointer">
            <RadioGroupItem value="inPerson" id="inPerson" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="inPerson" className="flex items-center cursor-pointer">
                <MapPin className="h-4 w-4 mr-2 text-primary" />
                <span>In-Person Meeting</span>
              </Label>
              <div className="text-xs text-muted-foreground mt-1 ml-6">
                <p>Meeting at:</p>
                {locations.map((location, index) => (
                  <p key={index} className="mt-1">{location}</p>
                ))}
              </div>
            </div>
          </div>
        )}
      </RadioGroup>
    </Card>
  );
};

export default MeetingTypeSelector;
